<template>
  <div
    class="my-3 p-4 rounded primary-shadow"
  >
    <div class="flex justify-between">
      <div class="flex justify-start">
        <div class="text-xl font-bold mb-2">
          <div
            v-if="!editingName"
          >
            {{ categoryName }}
          </div>
          <input
            v-else
            class="rounded p-1 container shadow-lg"
            :value="editingNameValue"
            @input="editingNameValue = $event.target.value"
          >
        </div>
        <div
          v-if="!editingName"
        >
          <eva-icon
            class="cursor-pointer ml-4"
            name="edit-outline"
            fill="#8B80F9"
            @click="editTitle()"
          />
        </div>
        <div
          v-else
          class="flex justify-end bg-white ml-2 p-2 rounded"
        >
          <eva-icon
            class="cursor-pointer"
            name="close"
            fill="#FF7570"
            @click="cancelEditTitle()"
          />
          <eva-icon
            class="cursor-pointer ml-4"
            name="save"
            fill="#8B80F9"
            @click="saveTitle()"
          />
        </div>
      </div>
      <div>
        <eva-icon
          class="cursor-pointer"
          name="collapse-outline"
          fill="#8B80F9"
          @click="$emit('collapse')"
        />
        <eva-icon
          v-if="categoryID !== 'symptoms'"
          class="cursor-pointer ml-4"
          name="trash"
          fill="#FF7570"
          @click="deleteCategory()"
        />
      </div>
    </div>
    <div class="flex mb-5">
      <div
        class="flex mr-5"
      >
        <div class="mr-2 text-lg">
          Obligatoria
        </div>
        <f7-toggle
          :checked="category.mandatory"
          @toggle:change="handleMandatory"
        />
      </div>
      <div
        class="flex"
      >
        <div class="mr-2 text-lg">
          Selección única
        </div>
        <f7-toggle
          :checked="category.singleAnswer"
          @toggle:change="handleSingleAnswer"
        />
      </div>
    </div>
    <div class="shadow p-5">
      <div
        class="flex"
      >
        <div class="mr-2 text-lg">
          Por etiquetas
        </div>
        <f7-toggle
          :checked="category.byTags"
          @toggle:change="handleByTags"
        />
      </div>
      <div v-if="category.byTags">
        <tag-manager
          class="mb-5"
          :disabled="saving"
          :success="success"
          :selectableTags="tags"
          :currentTags="currentTags"
          :rules="{ regex: /^[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF\-' ]*$/ }"
          @tag:add="addTag"
          @tag:remove="removeTag"
          @state:search="isSearching = $event"
        >
          Etiquetas para categoría:
        </tag-manager>
      </div>
    </div>
    <div class="shadow p-5">
      <div
        class="flex"
      >
        <div class="mr-2 text-lg">
          Por días
        </div>
        <f7-toggle
          :checked="category.byDays"
          @toggle:change="handleByDays"
        />
      </div>
      <vc-date-picker
        v-if="category.byDays"
        class="shadow-md"
        :value="dates"
        color="purple"
        :attributes="attributes"
        @dayclick="onDayClick"
      />
    </div>
    <div class="flex justify-between pb-2 mt-5">
      <div class="flex justify-start">
        <div class="text-lg font-bold mb-5">
          Preguntas
        </div>
        <eva-icon
          class="cursor-pointer ml-4"
          name="plus-circle"
          fill="#8B80F9"
          @click="createNewField()"
        />
      </div>
      <a
        v-if="selectedItems.size"
        class="cursor-pointer bg-red-500 text-white flex px-4 py-2 rounded-md items-center text-md mx-2"
        @click="deleteItems"
      >
        <eva-icon
          name="trash-2-outline"
          fill="#FFFFFF"
        />
        <div
          class="block-inline text-center w-full"
        >
          Eliminar
        </div>
      </a>
      <div
        v-if="isCreating"
        class="flex"
      >
        <a
          class="cursor-pointer flex px-4 py-2 rounded-md items-center text-md mx-2"
          @click="isCreating = false; weightToCreate = 0"
        >
          <eva-icon
            name="close-outline"
            fill="#282A43"
          />
          <div
            class="block-inline text-center w-full"
          >
            Cancelar
          </div>
        </a>
        <button
          class="cursor-pointer text-white bg-green-500 flex px-4 py-2 rounded-md items-center text-md disabled:opacity-25"
          :disabled="!isValidForm"
          @click="saveField()"
        >
          <eva-icon
            name="save-outline"
            fill="#FFFFFF"
          />
          <div
            class="block-inline text-center w-full"
          >
            Guardar
          </div>
        </button>
      </div>
    </div>
    <div>
      <div
        ref="tableContainer"
        class="overflow-y-auto overflow-x-hidden"
      >
        <table
          id="table"
          class="w-full rounded-lg"
        >
          <thead>
            <tr
              class="h-6 divide-x"
            >
              <th
                class="sticky top-0 text-left pl-2 text-white bg-black w-20"
              >
                Índice
              </th>
              <th
                class="sticky top-0 text-left pl-2 text-white bg-black"
              >
                Pregunta
              </th>
              <th
                class="sticky top-0 text-left px-2 text-white bg-black"
              >
                Nivel de riesgo
              </th>
            </tr>
          </thead>
          <tbody @mouseleave="activeHover = null">
            <tr
              v-if="isCreating"
              class="bg-purple-100 leading-9 cursor-pointer"
            >
              <td>
                <div
                  class="p-2 mx-auto w-4"
                >
                  -
                </div>
              </td>
              <td class="px-1 py-2">
                <input
                  ref="new-field"
                  v-model.trim="labelToCreate"
                  type="text"
                  tabindex="1"
                  required
                  class="rounded w-full px-2 text-center"
                >
              </td>
              <td class="px-1 py-2 text-center">
                <select
                  :class="`cursor-pointer ${getWeightClass(weightToCreate)}`"
                  :value="weightToCreate"
                  @input="weightToCreate = Number($event.target.value)"
                >
                  <option
                    :class="getWeightClass(0)"
                    :value="0"
                  >
                    0 - Sin riesgo
                  </option>
                  <option
                    :class="getWeightClass(1)"
                    :value="1"
                  >
                    1 - Riesgo bajo
                  </option>
                  <option
                    :class="getWeightClass(2)"
                    :value="2"
                  >
                    2 - Riesgo medio
                  </option>
                  <option
                    :class="getWeightClass(3)"
                    :value="3"
                  >
                    3 - Acceso restringido
                  </option>
                </select>
              </td>
            </tr>
            <tr
              v-for="(fieldID, index) of fieldIDs"
              :key="fieldID"
              :class="parseRowStyle(index)"
              @mouseenter="activeHover = fieldID"
              @mouseleave="activeHover = false"
            >
              <td class="relative first-col">
                <div
                  style="right:-18px; margin-top: -10px"
                  :class="`cursor-move absolute inline ml-4 shadow-lg rounded-full p-2 bg-white border border-gray-200 hover:border-purple-500 opacity-${activeHover === fieldID ? '100' : '25'}`"
                >
                  <eva-icon
                    class="align-middle pointer-events-none"
                    name="menu-outline"
                    fill="#8B80F9"
                  />
                </div>
                <input
                  v-if="(activeHover === fieldID || selectedItems.has(fieldID))"
                  type="checkBox"
                  class="cursor-pointer"
                  :checked="selectedItems.has(fieldID)"
                  @click="handleCheckBox(fieldID)"
                  @mousedown.prevent="isCheckbox = true"
                >
                <span>
                  {{ index + 1 }}
                </span>
              </td>
              <td
                v-for="(column, columnID) in columns"
                :key="columnID"
                class="p-1 border"
              >
                <div>
                  <div
                    v-if="column.inlineEditable && columnID !== 'weight'"
                    class="p-1 border border-dashed border-gray-500 border-opacity-0 hover:border-opacity-100 cursor-pointer h-full"
                    @click="startEditingField(fieldID, columnID)"
                  >
                    <input
                      v-if="columnID !== 'weight' && editingField.id === fieldID && editingField.column === columnID"
                      :ref="`${editingField.id}_${columnID}`"
                      type="text"
                      :value="editingValue"
                      class="rounded w-full text-center border-2 border-purple-500 border-opacity-0 focus:border-opacity-100"
                      @input="editingValue = $event.target.value"
                      @keyup.enter="stopEditing(fieldID, columnID)"
                      @blur="stopEditing(fieldID, columnID)"
                    >
                    <div
                      v-else
                      :class="(isSaving.id === fieldID && isSaving.column === columnID) ? 'saving': ''"
                    >
                      {{ category.fields[fieldID][columnID] }}
                    </div>
                  </div>
                  <div
                    v-else-if="columnID === 'weight'"
                  >
                    <select
                      :ref="`${fieldID}_${columnID}`"
                      :class="`cursor-pointer ${getWeightClass(editingField.id === fieldID ? editingValue : category.fields[fieldID][columnID])}`"
                      :value="editingField.id === fieldID ? editingValue : category.fields[fieldID][columnID]"
                      @focus="startEditingField(fieldID, columnID)"
                      @input="editingValue = $event.target.value; stopEditing(fieldID, columnID)"
                    >
                      <option
                        :class="getWeightClass(0)"
                        :value="0"
                      >
                        0 - Sin riesgo
                      </option>
                      <option
                        :class="getWeightClass(1)"
                        :value="1"
                      >
                        1 - Riesgo bajo
                      </option>
                      <option
                        :class="getWeightClass(2)"
                        :value="2"
                      >
                        2 - Riesgo medio
                      </option>
                      <option
                        :class="getWeightClass(3)"
                        :value="3"
                      >
                        3 - Inhabilitante
                      </option>
                    </select>
                  </div>
                  <div
                    v-else
                    class="p-1 border border-gray-500 border-opacity-0 cursor-not-allowed"
                  >
                    {{ field[columnID] }}
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import * as axios from 'axios'
import moment from 'moment-timezone'
import tagManager from '../components/tagManager'
// import { db } from '../utils/firebase'

export default {
  name: 'CategoryCard',
  components: {
    tagManager,
  },
  props: {
    categoryID: { type: String, default: '' },
    category: {
      default: function () {
        return { }
      },
      type: [Object, String],
    },
  },
  data () {
    return {
      editingValues: {},
      isEditing: {},
      isCreating: false,
      editingName: false,
      editingNameValue: '',
      newValue: '',
      activeHover: null,
      selectedItems: new Set(),
      labelToCreate: '',
      weightToCreate: 0,
      columns: {
        label: { label: 'Pregunta', inlineEditable: true },
        weight: { label: 'Peso', inlineEditable: true },
      },
      editingField: {},
      isSaving: {},
      isStoping: false,
      editingValue: '',
      tagInput: null,
      inputValue: '',
      success: false,
      saving: false,
      table: null,
      draggingEle: null,
      draggingRowIndex: null,
      endRowIndex: null,
      placeholder: null,
      list: null,
      isDraggingStarted: false,
      x: 0,
      y: 0,
      isCheckbox: false,
      addedNew: false,
      currentScroll: 0,
      startingScroll: 0,
    }
  },
  computed: {
    dates () {
      if (this.days) {
        return this.days.map(day => day.date)
      }
      return []
    },
    days () {
      return this.category.days || []
    },
    attributes () {
      return this.dates.map(date => ({
        highlight: true,
        dates: date,
      }))
    },
    tagsArray () {
      return Object.keys(this.tags).map(tagID => {
        return this.tags[tagID]
      }).sort()
    },
    currentTags () {
      const tags = {}
      if (!this.category.tagIDs) {
        return tags
      }
      for (const tagID of Object.keys(this.category.tagIDs)) {
        tags[tagID] = this.tags[tagID]
      }
      return tags
    },
    categoryName () {
      return this.category.name
    },
    fieldIDs () {
      return this.category.fieldIDs || []
    },
    filteredPersons () {
      const found = []
      for (let i = 0; i < this.persons.length; i++) {
        if (typeof this.persons[i].name !== 'undefined' && this.persons[i].name) {
          if (this.persons[i].name.toLowerCase().indexOf(this.searchValue.toLowerCase()) >= 0 || this.searchValue.trim() === '') found.push(this.persons[i])
        }
      }
      return found
    },
    isValidForm () {
      const requiredValid = this.labelToCreate !== '' && this.weightToCreate !== ''
      return requiredValid
    },
    ...mapState({
      instanceID: state => state.admin.instanceID,
      instancesInfo: state => state.auth.instancesInfo,
    }),
    ...mapGetters({
      tags: 'admin/tags',
    }),
  },
  watch: {
    fields: function () {
      this.selectedItems.clear()
    },
    fieldIDs () {
      if (this.addedNew) {
        this.addedNew = false
        this.$nextTick(() => {
          const rows = this.table.querySelectorAll('tr')
          const lastRow = [].slice.call(rows).pop()
          const firstCell = lastRow.firstElementChild.firstElementChild
          firstCell.classList.add('draggable')

          // Attach event handler
          firstCell.addEventListener('mousedown', this.mouseDownHandler)
        })
      }
    },
  },
  async mounted () {
    this.table = document.getElementById('table')
    for (const [index, row] of this.table.querySelectorAll('tr').entries()) {
      // Ignore the header
      // We don't want user to change the order of header
      if (index !== 0) {
        // Get the first cell of row
        const firstCell = row.firstElementChild.firstElementChild
        firstCell.classList.add('draggable')

        // Attach event handler
        firstCell.addEventListener('mousedown', this.mouseDownHandler)
      }
    }
    if (this.$$(document).find('.page-content')) {
      const currentPage = this.$$(document).find('.page-content')[0]
      this.currentScroll = currentPage.scrollTop
      currentPage.addEventListener('scroll', () => {
        this.currentScroll = currentPage.scrollTop
      })
    }
  },
  methods: {
    swap (nodeA, nodeB) {
      const parentA = nodeA.parentNode
      const siblingA = nodeA.nextSibling === nodeB ? nodeA : nodeA.nextSibling

      // Move `nodeA` to before the `nodeB`
      nodeB.parentNode.insertBefore(nodeA, nodeB)

      // Move `nodeB` to before the sibling of `nodeA`
      parentA.insertBefore(nodeB, siblingA)
    },
    // Check if `nodeA` is above `nodeB`
    isAbove (nodeA, nodeB) {
      // Get the bounding rectangle of nodes
      const rectA = nodeA.getBoundingClientRect()
      const rectB = nodeB.getBoundingClientRect()

      return (rectA.top + rectA.height / 2 < rectB.top + rectB.height / 2)
    },
    mouseDownHandler (e) {
      if (this.isCheckbox) {
        this.isCheckbox = false
        return
      }
      // Get the original row
      const originalRow = e.target.parentNode.parentNode
      this.draggingRowIndex = [].slice.call(this.table.querySelectorAll('tr')).indexOf(originalRow)

      // Determine the mouse position
      this.x = e.pageX
      this.y = e.pageY
      this.startingScroll = this.currentScroll

      // Attach the listeners to `document`
      document.addEventListener('mousemove', this.mouseMoveHandler)
      document.addEventListener('mouseup', this.mouseUpHandler)
    },
    mouseMoveHandler (e) {
      if (!this.isDraggingStarted) {
        this.isDraggingStarted = true

        this.cloneTable()

        this.draggingEle = [].slice.call(this.list.children)[this.draggingRowIndex]
        this.draggingEle.classList.add('dragging')
        this.draggingEle.style.backgroundColor = '#fff'
        this.draggingEle.style.boxShadow = '0px 0px 15px #8B80F9'

        // Let the placeholder take the height of dragging element
        // So the next element won't move up
        this.placeholder = document.createElement('div')
        this.placeholder.classList.add('placeholder')
        this.draggingEle.parentNode.insertBefore(this.placeholder, this.draggingEle.nextSibling)
        this.placeholder.style.height = `${this.draggingEle.offsetHeight}px`
        this.placeholder.style.backgroundColor = '#E8E7FF'
        this.placeholder.style.border = '3px dashed #8B80F9'
      }

      // Set position for dragging element
      const scrollOffset = this.currentScroll - this.startingScroll
      this.startingScroll = this.currentScroll
      this.draggingEle.style.position = 'absolute'
      this.draggingEle.style.top = `${this.draggingEle.offsetTop + e.pageY + scrollOffset - document.body.scrollTop - this.y}px`
      this.draggingEle.style.left = `${this.draggingEle.offsetLeft + e.pageX - this.x}px`

      // Reassign the position of mouse
      this.x = e.pageX
      this.y = e.pageY

      // The current order
      // prevEle
      // draggingEle
      // placeholder
      // nextEle
      const prevEle = this.draggingEle.previousElementSibling
      const nextEle = this.placeholder.nextElementSibling

      // The dragging element is above the previous element
      // User moves the dragging element to the top
      // We don't allow to drop above the header
      // (which doesn't have `previousElementSibling`)
      if (prevEle && prevEle.previousElementSibling && this.isAbove(this.draggingEle, prevEle)) {
        // The current order    -> The new order
        // prevEle              -> placeholder
        // draggingEle          -> draggingEle
        // placeholder          -> prevEle
        this.swap(this.placeholder, this.draggingEle)
        this.swap(this.placeholder, prevEle)
        return
      }

      // The dragging element is below the next element
      // User moves the dragging element to the bottom
      if (nextEle && this.isAbove(nextEle, this.draggingEle)) {
        // The current order    -> The new order
        // draggingEle          -> nextEle
        // placeholder          -> placeholder
        // nextEle              -> draggingEle
        this.swap(nextEle, this.placeholder)
        this.swap(nextEle, this.draggingEle)
      }
    },
    async mouseUpHandler (e) {
      if (!this.isDraggingStarted && this.list) {
        this.draggingEle = [].slice.call(this.list.children)[this.draggingRowIndex]
      }
      // Remove the placeholder
      this.placeholder && this.placeholder.parentNode && this.placeholder.parentNode.removeChild(this.placeholder)

      if (this.draggingEle) {
        this.draggingEle.classList.remove('dragging')
        this.draggingEle.style.removeProperty('top')
        this.draggingEle.style.removeProperty('left')
        this.draggingEle.style.removeProperty('position')
      }

      // Get the end index
      if (this.list) {
        this.endRowIndex = [].slice.call(this.list.children).indexOf(this.draggingEle)
      } else {
        this.endRowIndex = this.draggingRowIndex
      }

      this.isDraggingStarted = false

      // Remove the `list` element
      this.list && this.list.parentNode && this.list.parentNode.removeChild(this.list)

      // Move the dragged row to `endRowIndex`
      // Bring back the table
      this.table.style.removeProperty('display')
      // this.table.style.removeProperty('position')
      this.sortFieldIDs()

      // Remove the handlers of `mousemove` and `mouseup`
      document.removeEventListener('mousemove', this.mouseMoveHandler)
      document.removeEventListener('mouseup', this.mouseUpHandler)
    },
    cloneTable () {
      const width = parseInt(window.getComputedStyle(this.table).width)

      this.list = document.createElement('div')
      this.list.classList.add('clone-list')
      this.table.parentNode.insertBefore(this.list, this.table)

      // this.table.style.position = 'absolute'

      for (const row of this.table.querySelectorAll('tr')) {
        // Create a new table from given row
        const item = document.createElement('div')
        item.classList.add('draggable')

        const newTable = document.createElement('table')
        newTable.setAttribute('class', 'clone-table')
        newTable.style.width = `${width}px`

        const newRow = document.createElement('tr')
        const cells = [].slice.call(row.children)
        for (const cell of cells) {
          const newCell = cell.cloneNode(true)
          // Set the width as the original cell
          const styles = window.getComputedStyle(cell)
          if (styles.cssText && styles.cssText !== '') {
            newCell.style.cssText = styles.cssText
          } else {
            const cssText = Object.values(styles).reduce(
              (css, propertyName) =>
                `${css}${propertyName}:${styles.getPropertyValue(
                    propertyName
                )};`
            )
            newCell.style.cssText = cssText
          }
          // newCell.style.visibility = 'visible'
          newRow.appendChild(newCell)
        }

        newTable.appendChild(newRow)
        item.appendChild(newTable)
        this.list.appendChild(item)
      }
      // Hide the original table
      this.table.style.display = 'none'
    },
    async sortFieldIDs (tagID) {
      try {
        if (this.draggingRowIndex === this.endRowIndex) {
          return
        }
        this.$f7.preloader.show('Actualizando categoría...')
        this.success = false
        const fieldIDs = JSON.parse(JSON.stringify(this.fieldIDs))
        if (this.endRowIndex > this.draggingRowIndex) {
          fieldIDs.splice(this.endRowIndex, 0, fieldIDs[this.draggingRowIndex - 1])
          fieldIDs.splice(this.draggingRowIndex - 1, 1)
        } else {
          fieldIDs.splice(this.endRowIndex - 1, 0, fieldIDs[this.draggingRowIndex - 1])
          fieldIDs.splice(this.draggingRowIndex, 1)
        }
        const response = await axios({
          url: '/instances/form/field/sort',
          method: 'POST',
          data: {
            categoryID: this.categoryID,
            fieldIDs,
            instanceID: this.instanceID,
          },
        })
        this.success = true
        this.$f7.preloader.hide()
        console.info(response)
      } catch (err) {
        this.$f7.preloader.hide()
        console.error(err)
      }
    },
    async addTag (tagID) {
      try {
        this.$f7.preloader.show('Actualizando categoría...')
        this.success = false
        const response = await axios({
          url: '/instances/form/addTag',
          method: 'POST',
          data: {
            categoryID: this.categoryID,
            tagID,
            instanceID: this.instanceID,
          },
        })
        this.success = true
        this.$f7.preloader.hide()
        console.info(response)
      } catch (err) {
        this.$f7.preloader.hide()
        console.error(err)
      }
    },
    async removeTag (tagID) {
      try {
        this.$f7.preloader.show('Actualizando categoría...')
        this.success = false
        const response = await axios({
          url: '/instances/form/removeTag',
          method: 'POST',
          data: {
            categoryID: this.categoryID,
            tagID,
            instanceID: this.instanceID,
          },
        })
        this.success = true
        this.$f7.preloader.hide()
        console.info(response)
      } catch (err) {
        this.$f7.preloader.hide()
        console.error(err)
      }
    },
    async onDayClick (day) {
      try {
        this.$f7.preloader.show('Actualizando categoría...')
        day.id = moment(day.date).tz('America/Bogota').startOf('day').format()
        const idx = this.days.findIndex(d => d.id === day.id)
        let response
        if (idx >= 0) {
          response = await axios({
            url: '/instances/form/removeDay',
            method: 'POST',
            data: {
              categoryID: this.categoryID,
              day: day,
              instanceID: this.instanceID,
            },
          })
        } else {
          response = await axios({
            url: '/instances/form/addDay',
            method: 'POST',
            data: {
              categoryID: this.categoryID,
              day: day,
              instanceID: this.instanceID,
            },
          })
        }
        this.$f7.preloader.hide()
        console.info(response)
      } catch (err) {
        this.$f7.preloader.hide()
        console.error(err)
      }
    },
    async handleSingleAnswer (singleAnswer) {
      try {
        this.$f7.preloader.show('Actualizando categoría...')
        const response = await axios({
          url: '/instances/form/singleAnswer',
          method: 'POST',
          data: {
            categoryID: this.categoryID,
            singleAnswer,
            instanceID: this.instanceID,
          },
        })
        this.$f7.preloader.hide()
        console.info(response)
      } catch (err) {
        this.$f7.preloader.hide()
        console.error(err)
      }
    },
    async handleByTags (byTags) {
      try {
        this.$f7.preloader.show('Actualizando categoría...')
        const response = await axios({
          url: '/instances/form/byTags',
          method: 'POST',
          data: {
            categoryID: this.categoryID,
            byTags,
            instanceID: this.instanceID,
          },
        })
        this.$f7.preloader.hide()
        console.info(response)
      } catch (err) {
        this.$f7.preloader.hide()
        console.error(err)
      }
    },
    async handleMandatory (mandatory) {
      try {
        this.$f7.preloader.show('Actualizando categoría...')
        const response = await axios({
          url: '/instances/form/mandatory',
          method: 'POST',
          data: {
            categoryID: this.categoryID,
            mandatory,
            instanceID: this.instanceID,
          },
        })
        this.$f7.preloader.hide()
        console.info(response)
      } catch (err) {
        this.$f7.preloader.hide()
        console.error(err)
      }
    },
    async handleByDays (byDays) {
      try {
        this.$f7.preloader.show('Actualizando categoría...')
        const response = await axios({
          url: '/instances/form/byDays',
          method: 'POST',
          data: {
            categoryID: this.categoryID,
            byDays,
            instanceID: this.instanceID,
          },
        })
        this.$f7.preloader.hide()
        console.info(response)
      } catch (err) {
        this.$f7.preloader.hide()
        console.error(err)
      }
    },
    getWeightClass (weight) {
      let style = 'p-2 font-bold text-white'
      if (weight === 0) {
        style += ' bg-green-500'
      }
      if (weight === 1) {
        style += ' bg-yellow-500'
      }
      if (weight === 2) {
        style += ' bg-orange-500'
      }
      if (weight === 3) {
        style += ' bg-red-500'
      }
      return style
    },
    stopEditing (fieldID, columnID) {
      if (!this.isStoping) {
        this.isStoping = true
        if (this.editingValue !== '' && this.category.fields[fieldID][columnID] !== this.editingValue) {
          this.saveEdit()
        } else {
          this.$set(this.editingField, 'id', null)
          this.$set(this.editingField, 'column', null)
          this.editingValue = ''
          this.isStoping = false
        }
      }
    },
    async saveEdit () {
      this.$set(this.isSaving, 'id', this.editingField.id)
      this.$set(this.isSaving, 'column', this.editingField.column)
      const field = this.category.fields[this.editingField.id]
      field[this.editingField.column] = this.editingField.column === 'weight' ? Number(this.editingValue) : this.editingValue
      console.info({
        fieldID: this.editingField.id,
        weight: field.weight,
        instanceID: this.instanceID,
        categoryID: this.categoryID,
        label: field.label,
      })
      try {
        const response = await axios({
          url: '/instances/form/field/update',
          method: 'POST',
          data: {
            fieldID: this.editingField.id,
            weight: field.weight,
            instanceID: this.instanceID,
            categoryID: this.categoryID,
            label: field.label,
          },
        })
        console.info(response)
      } catch (err) {
        console.error(err)
      } finally {
        if (this.$refs[`${this.editingField.id}_${this.editingField.column}`]) {
          this.$refs[`${this.editingField.id}_${this.editingField.column}`][0].blur()
        }
        this.$set(this.editingField, 'id', null)
        this.$set(this.editingField, 'column', null)
        this.$set(this.isSaving, 'id', null)
        this.$set(this.isSaving, 'column', null)
        this.editingValue = ''
        this.isStoping = false
      }
    },
    startEditingField (fieldID, columnID) {
      this.$set(this.editingField, 'id', fieldID)
      this.$set(this.editingField, 'column', columnID)
      this.editingValue = this.category.fields[fieldID][columnID]
      if (columnID !== 'weight') {
        this.$nextTick(() => {
          if (this.$refs[`${fieldID}_${columnID}`]) {
            this.$refs[`${fieldID}_${columnID}`][0].focus()
          }
        })
      }
    },
    parseRowStyle (index) {
      let base = 'text-center hover:bg-purple-200 hover:text-purple-500 '
      base += `${index % 2 ? 'bg-gray-100 ' : ''}`
      return base
    },
    handleCheckBox (fieldID) {
      if (this.selectedItems.has(fieldID)) {
        this.selectedItems.delete(fieldID)
      } else {
        this.selectedItems.add(fieldID)
      }
      this.$forceUpdate()
    },
    async deleteItems () {
      this.$f7.dialog.preloader('Eliminando preguntas...')
      try {
        const response = await axios({
          url: '/instances/form/field/remove',
          method: 'POST',
          data: {
            instanceID: this.instanceID,
            categoryID: this.categoryID,
            fieldIDs: Array.from(this.selectedItems),
          },
        })
        console.info(response)
      } catch (err) {
        console.error(err)
      } finally {
        this.$f7.dialog.close()
        this.selectedItems.clear()
      }
    },
    async deleteCategory () {
      this.$f7.dialog.confirm(`¿Estás seguro de que deseas eliminar la categoría <span class="text-purple-500 font-bold">${this.categoryName}</span>?`, '¿Seguro?', async () => {
        try {
          this.$f7.preloader.show('Actualizando preguntas...')
          const response = await axios({
            url: '/instances/form/category/delete',
            method: 'POST',
            data: {
              categoryID: this.categoryID,
              instanceID: this.instanceID,
            },
          })
          this.$f7.preloader.hide()
          console.info(response)
        } catch (err) {
          this.$f7.preloader.hide()
          console.error(err)
        }
      })
    },
    createNewField () {
      this.isCreating = true
      this.weightToCreate = 0
      this.label = ''
      this.$nextTick(() => {
        this.$refs['new-field'].scrollIntoView()
        this.$refs['new-field'].focus()
      })
    },
    edit (fieldID) {
      this.$set(this.isEditing, fieldID, true)
      this.$set(this.editingValues, fieldID, this.category.fields[fieldID])
    },
    editTitle () {
      this.editingName = true
      this.editingNameValue = this.categoryName
    },
    cancelEditTitle () {
      this.editingName = false
      this.editingNameValue = ''
    },
    async saveField (fieldID) {
      try {
        this.$f7.preloader.show('Actualizando preguntas...')
        const response = await axios({
          url: '/instances/form/field/update',
          method: 'POST',
          data: {
            ...(fieldID && { fieldID }),
            instanceID: this.instanceID,
            categoryID: this.categoryID,
            label: this.labelToCreate,
            weight: this.weightToCreate,
          },
        })
        this.$f7.preloader.hide()
        if (!fieldID) {
          this.isCreating = false
          this.newValue = ''
        }
        this.labelToCreate = ''
        this.weightToCreate = 0
        this.addedNew = true
        console.info(response)
      } catch (err) {
        this.$f7.preloader.hide()
        console.error(err)
      }
      this.$set(this.isEditing, fieldID, false)
    },
    async saveTitle () {
      try {
        this.$f7.preloader.show('Actualizando categoría...')
        const response = await axios({
          url: '/instances/form/categoryName/update',
          method: 'POST',
          data: {
            instanceID: this.instanceID,
            categoryID: this.categoryID,
            categoryName: this.editingNameValue,
          },
        })
        this.editingName = false
        this.editingNameValue = ''
        this.$f7.preloader.hide()
        console.info(response)
      } catch (err) {
        this.$f7.preloader.hide()
        console.error(err)
      }
    },
    async removeField (fieldID) {
      try {
        this.$f7.dialog.confirm(`¿Estás seguro de que deseas eliminar la pregunta <span class="font-bold text-purple-500">${this.category.fields[fieldID]}</span>`, async () => {
          this.$f7.preloader.show('Actualizando preguntas...')
          const response = await axios({
            url: '/instances/form/field/remove',
            method: 'POST',
            data: {
              fieldID,
              instanceID: this.instanceID,
              categoryID: this.categoryID,
            },
          })
          this.$f7.preloader.hide()
          console.info(response)
        })
      } catch (err) {
        this.$f7.preloader.hide()
        console.error(err)
      }
      this.$set(this.isEditing, fieldID, false)
    },
    cancel (fieldID) {
      this.$set(this.isEditing, fieldID, false)
      this.$set(this.editingValues, fieldID, this.category.fields[fieldID])
    },
  },
}
</script>
<style lang="scss" scoped>
  @import "../css/_brand";
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  .table {
      border: 1px solid #ccc;
      border-collapse: collapse;
  }
  .table th, .table td {
      border: 1px solid #ccc;
  }
  .table th, .table td {
      padding: 0.5rem;
  }
  .draggable {
      cursor: move;
      user-select: none;
  }
  .clone-list {
      border-top: 1px solid #ccc;
  }
  .clone-table {
      border-collapse: collapse;
      border: none;
  }
  .clone-table th, .clone-table td {
      border: 1px solid #ccc;
      border-top: none;
      padding: 0.5rem;
  }
  .dragging {
      background: #fff;
      border-top: 1px solid #ccc;
      z-index: 999;
  }
</style>
